/**
 * ProgressBar Component
 * Generic progress bar for displaying processing progress
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import './ProgressBar.css';

export interface ProgressBarProps {
  progressPercent: number;
  statusMessage?: string;
  statusKey?: string;
  statusParams?: Record<string, string | number>;
  currentItem?: number;
  totalItems?: number;
  showPercentage?: boolean;
}

const ProgressBar: React.FC<ProgressBarProps> = ({
  progressPercent,
  statusMessage,
  statusKey,
  statusParams,
  currentItem,
  totalItems,
  showPercentage = true,
}) => {
  const { t } = useTranslation('creator');

  // Ensure progress is between 0 and 100, round to integer for display
  const normalizedProgress = Math.min(100, Math.max(0, progressPercent));
  const displayProgress = Math.round(normalizedProgress);

  // Use i18n translation if status_key is provided, fallback to raw status_message
  const displayMessage = statusKey
    ? t(`workflow.statusMessages.${statusKey}`, { ...statusParams, defaultValue: statusMessage || '' })
    : statusMessage;

  return (
    <div className="progress-bar-container">
      <div className="progress-bar-wrapper">
        <div
          className="progress-bar-fill"
          style={{ width: `${normalizedProgress}%` }}
        >
          {showPercentage && (
            <span className="progress-bar-percentage">
              {displayProgress}%
            </span>
          )}
        </div>
      </div>
      <div className="progress-bar-info">
        {displayMessage && (
          <span className="progress-bar-status">{displayMessage}</span>
        )}
        {currentItem !== undefined && totalItems !== undefined && (
          <span className="progress-bar-count">
            {currentItem} / {totalItems}
          </span>
        )}
      </div>
    </div>
  );
};

export default ProgressBar;
